import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Save, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import type { Bridgelet } from '../utils/api';
import { bridgeletApi } from '../utils/api';

interface BridgeletEditPageProps {
  bridgelet: Bridgelet;
  onBack: () => void;
  onSave: (updatedBridgelet: Bridgelet) => void;
}

export function BridgeletEditPage({ bridgelet, onBack, onSave }: BridgeletEditPageProps) {
  const currentInstructions = (bridgelet.persona as any).customInstructions || '';
  const [customInstructions, setCustomInstructions] = useState(currentInstructions);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<any[] | null>(null);
  const [success, setSuccess] = useState(false);

  const maxLength = 500;
  const currentLength = customInstructions.length;
  const isValid = currentLength <= maxLength;
  const hasChanged = customInstructions !== currentInstructions;

  const handleSave = async () => {
    if (!isValid) {
      setError('คำสั่งต้องไม่เกิน 500 ตัวอักษร');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setViolations(null);
      setSuccess(false);

      const response = await bridgeletApi.updateInstructions(customInstructions);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSave(response.bridgelet);
        }, 1500);
      }
    } catch (err: any) {
      const errorData = err.response?.data;

      if (errorData?.violations) {
        // BridgeGuard blocked the content
        setViolations(errorData.violations);
        setError(errorData.educationalMessage || 'เนื้อหาไม่เหมาะสม กรุณาแก้ไขและลองอีกครั้ง');
      } else {
        setError(errorData?.error || err.message || 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <button
          onClick={onBack}
          className="p-2 rounded-xl glass hover:bg-white/15 transition-all active:scale-95"
          disabled={isSaving}
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <h1 className="text-2xl font-bold text-white">ปรับแต่งบุคลิกภาพ</h1>
        <div className="w-10" />
      </motion.div>

      {/* Custom Instructions Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-6 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            คำสั่งพิเศษสำหรับ AI Partner
          </label>
          <p className="text-sm text-white/60 mb-4">
            ระบุวิธีการเจรจา ความชอบพิเศษ หรือกฎที่คุณต้องการให้ AI ทำตาม
          </p>

          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="ตัวอย่าง: ฉันชอบเจรจาแบบค่อยเป็นค่อยไป ไม่เร่งรีบ และต้องการให้มีการถามยืนยันก่อนตกลงทุกครั้ง..."
            disabled={isSaving}
            className={`w-full h-40 p-4 rounded-xl glass text-white placeholder:text-white/40 resize-none focus:outline-none focus:ring-2 ${
              !isValid
                ? 'focus:ring-red-500/50 border-2 border-red-400/50'
                : 'focus:ring-teal-500/50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          />
        </div>

        {/* Character Counter */}
        <div className="flex items-center justify-between text-sm">
          <span className={currentLength > maxLength ? 'text-red-400' : 'text-white/60'}>
            {currentLength} / {maxLength} ตัวอักษร
          </span>
          {currentLength > maxLength && (
            <span className="text-red-400 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              เกินจำนวนที่กำหนด
            </span>
          )}
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min((currentLength / maxLength) * 100, 100)}%` }}
            className={`h-full transition-colors ${
              currentLength > maxLength
                ? 'bg-gradient-to-r from-red-500 to-red-600'
                : currentLength > maxLength * 0.9
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                : 'bg-gradient-to-r from-teal-500 to-cyan-500'
            }`}
          />
        </div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-red-500/20 border border-red-400/30"
          >
            <p className="text-red-300 text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </p>

            {violations && violations.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-red-300 text-xs font-semibold">ปัญหาที่พบ:</p>
                {violations.map((violation, idx) => (
                  <div key={idx} className="text-red-300 text-xs pl-4">
                    • {violation.description} (ความมั่นใจ: {(violation.confidence * 100).toFixed(0)}%)
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Success Display */}
        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 rounded-xl bg-green-500/20 border border-green-400/30"
          >
            <p className="text-green-300 text-sm flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              บันทึกสำเร็จ! กำลังกลับหน้าหลัก...
            </p>
          </motion.div>
        )}

        {/* Save Button */}
        <motion.button
          onClick={handleSave}
          disabled={!hasChanged || !isValid || isSaving || success}
          whileTap={{ scale: hasChanged && isValid && !isSaving ? 0.95 : 1 }}
          className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
            hasChanged && isValid && !isSaving && !success
              ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-[0_0_30px_rgba(20,184,166,0.6)] hover:shadow-[0_0_40px_rgba(20,184,166,0.8)]'
              : 'glass text-white/40 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              กำลังบันทึก...
            </>
          ) : success ? (
            <>
              <CheckCircle className="w-5 h-5" />
              บันทึกสำเร็จ
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              บันทึกการเปลี่ยนแปลง
            </>
          )}
        </motion.button>

        {/* Help Text */}
        <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-400/20">
          <p className="text-teal-300 text-xs leading-relaxed">
            💡 <strong>เคล็ดลับ:</strong> AI Partner จะปฏิบัติตามคำสั่งเหล่านี้เมื่อเจรจาดีลของคุณ
            ระบบจะตรวจสอบเนื้อหาเพื่อความปลอดภัยและป้องกันพฤติกรรมที่ไม่เหมาะสม
          </p>
        </div>
      </motion.div>
    </div>
  );
}
